// SPDX-FileCopyrightText: Copyright (c) 2025 Software GmbH, Darmstadt, Germany and/or its subsidiaries and/or its affiliates
// SPDX-FileContributor: Zead Alshukairi (planen-bauen 4.0 GmbH)
//
// SPDX-License-Identifier: Apache-2.0

import { AbstractControl, ValidationErrors } from "@angular/forms";

export class DidValidators {

    private static readonly domainRegex = /^(?![a-zA-Z]+:\/\/)[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+$/;
    private static readonly urlRegex = /^(?![a-zA-Z]+:\/\/)[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+(?:\/[^\s\/]+)*\.[A-Za-z0-9]+$/;
    private static readonly pemOrCertUrl = /^(?![a-zA-Z]+:\/\/)[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+(?:\/[^\s\/]+)*\.(?:pem|cert)$/;

    /**
     * Validates that the control value is a valid domain.
     *
     * @param control the form control
     * @returns validation error or null
     */
    static isDomain(control: AbstractControl): ValidationErrors {
        if (control.value && !DidValidators.domainRegex.test(control.value)) {
            return { domainInvalid: true };
        }
        return null;
    }

    /**
     * Validates that at least one certificate source (file, URL, or SSL) is provided.
     *
     * @param control the form control
     * @returns validation error or null
     */
    static fileOrUrlOrSslRequired(control: AbstractControl): ValidationErrors {
        let fileControl = control.get('file');
        let urlControl = control.get('url');
        let sslControl = control.get('ssl');
        if (fileControl && urlControl && sslControl && !fileControl.value && !(urlControl.value?? '').toString().trim() && !sslControl.value) {
            return { fileOrUrlOrSslRequired: true };
        }
        return null;
    }

    /**
     * Validates that the file has a .pem or .crt extension.
     *
     * @param control the form control
     * @returns validation error or null
     */
    static correctExtension(control: AbstractControl): ValidationErrors {
        let file = control.value as File | null;
        if (file && !file.name.endsWith('.pem') && !file.name.endsWith('.crt')) {
            return { wrongExtension: true };
        }
        return null;
    }

    /**
     * Validates that the control value is a valid URL.
     *
     * @param control the form control
     * @returns validation error or null
     */
    static isUrl(control: AbstractControl): ValidationErrors {
        if (control.value && !DidValidators.urlRegex.test(control.value)) {
            return { urlInvalid: true };
        }
        return null;
    }

    /**
     * Validates that the URL ends with .pem or .cert extension.
     *
     * @param control the form control
     * @returns validation error or null
     */
    static correctUrlExtension(control: AbstractControl): ValidationErrors {
        if (control.value && !DidValidators.pemOrCertUrl.test(control.value)) {
            return { wrongUrlExtension: true };
        }
        return null;
    }
}